import React, { useState, useEffect, useRef } from 'react'
import { animated, useSpring } from '@react-spring/web'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import useRefDimensions from '../../hooks/useRefDimensions'
import preventEvents from '../../utils/preventEvents'
import { RetorikLogoIcon } from '../Icons/Miscellaneous'
import LoaderCallToAction from './LoaderCallToAction'
import GdprWindow from './GdprWindow'

interface GdprAndLoaderProps {
  appAvailable: boolean
  handleValidation: () => void
}

const springEnterDuration = 500
const springLeaveDuration = 1000

const GdprAndLoader = ({ appAvailable, handleValidation }: GdprAndLoaderProps): JSX.Element | null => {
  const dispatch = useDispatch()
  const displayGDPR = useSelector((state: RootState) => state.utilsReducer.GDPRDisplay)
  const isUsedOnBorne = useSelector((state: RootState) => state.retorikReducer.configuration.isUsedOnBorne)
  const skipLoader = useSelector((state: RootState) => state.retorikReducer.skipLoader)
  const loaderClosed = useSelector((state: RootState) => state.retorikReducer.loaderClosed)
  const hideRetorikLogo = useSelector((state: RootState) => state.retorikReducer.configuration.hideRetorikLogo)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const isWidget = useSelector((state: RootState) => state.viewReducer.isWidget)
  const themeColors = useSelector((state: RootState) => state.viewReducer.themeColors)

  const [textSize, setTextSize] = useState<string>('')
  const [titleTextSize, setTitleTextSize] = useState<string>()
  const [loaderDisplayData, setLoaderDisplayData] = useState<{
    display: boolean
    displayModeChoice?: boolean
    displayGDPR?: boolean
  }>({ display: false, displayModeChoice: false, displayGDPR: false })
  const container = useRef<HTMLDivElement>(null)
  const dimensions = useRefDimensions(container)
  const animationTimerRef = useRef<NodeJS.Timeout>()

  const [spring, api] = useSpring(() => ({
    from: {
      opacity: 0
    }
  }))

  useEffect(() => {
    // Check if we have to show the loader / GDPR / mode choice or directly the application
    if (loaderClosed || appAvailable) {
      setLoaderDisplayData({
        display: false
      })
    } else if (isUsedOnBorne || skipLoader) {
      if (navigator.userActivation.hasBeenActive) {
        dispatch(storeActions.utils.setGDPRDisplay(false))
        handleValidation()
      } else {
        setLoaderDisplayData({
          display: true,
          displayModeChoice: false,
          displayGDPR: false
        })
      }
    } else {
      setLoaderDisplayData({
        display: true,
        displayModeChoice: true,
        displayGDPR: displayGDPR
      })
    }
  }, [isUsedOnBorne, skipLoader, displayGDPR, appAvailable, loaderClosed])

  useEffect(() => {
    api.start({
      from: {
        opacity: 0
      },
      to: {
        opacity: 1
      },
      config: {
        duration: springEnterDuration
      }
    })

    return () => {
      animationTimerRef?.current && clearTimeout(animationTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (dimensions.width && dimensions.height && dimensions.width > 600) {
      if (dimensions.width < dimensions.height) {
        setTextSize('rf-text-lgcustom')
        setTitleTextSize('rf-text-3xl')
      } else {
        setTextSize('rf-text-basecustom')
        setTitleTextSize('rf-text-4xl')
      }
    } else {
      setTextSize('rf-text-sm')
      setTitleTextSize('rf-text-2xl')
    }
  }, [dimensions])

  const handleValidate = (): void => {
    handleValidation()
    api.start({
      from: {
        opacity: 1
      },
      to: {
        opacity: 0
      },
      config: {
        duration: springLeaveDuration
      }
    })
  }

  useEffect(() => {
    container?.current && isWidget && preventEvents(container.current)
  }, [container?.current])

  return loaderDisplayData.display ? (
    <animated.div
      id='retorik-GDPR-and-loader'
      className={`rf-absolute rf-z-modal rf-top-0 rf-left-0 rf-h-full rf-w-full rf-font-default
        ${textSize}`}
      ref={container}
      style={{
        background: themeColors.loader.background,
        color: themeColors.loader.text,
        ...spring
      }}
    >
      {loaderDisplayData.displayGDPR ? <GdprWindow titleTextSize={titleTextSize} /> : <LoaderCallToAction showContent={loaderDisplayData.displayModeChoice} handleValidation={handleValidate} />}

      {/* Logo */}
      {!hideRetorikLogo && (isMobile ? <RetorikLogoIcon className='rf-absolute rf-bottom-10 rf-h-14' /> : <RetorikLogoIcon className='rf-absolute rf-left-4 rf-bottom-4 rf-h-14' />)}
    </animated.div>
  ) : null
}

export default GdprAndLoader
